import { createClient } from "@supabase/supabase-js";
import type { Database } from "@/types/supabase";
import type { ThemeColor, BookType, CopyCondition } from "@/types/database";

/**
 * 🚨 NO FALLBACKS - READ START_HERE.md & VERCEL_ENV_SETUP.md
 *
 * Environment variables MUST be set in Vercel project settings.
 * If missing, this will throw a clear error.
 *
 * Build command passes them:
 * VITE_SUPABASE_URL=$VITE_SUPABASE_URL VITE_SUPABASE_ANON_KEY=$VITE_SUPABASE_ANON_KEY npm run build
 */
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL || "";
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY || "";

// Validate on client creation - fail loudly if missing
if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    `Missing Supabase configuration. Required environment variables not set:\n` +
      `- VITE_SUPABASE_URL: ${supabaseUrl ? "✓ set" : "❌ missing"}\n` +
      `- VITE_SUPABASE_ANON_KEY: ${supabaseAnonKey ? "✓ set" : "❌ missing"}\n\n` +
      `Fix: Set these in Vercel project settings. See VERCEL_ENV_SETUP.md for checklist.`,
  );
}

export const supabase = createClient<Database>(supabaseUrl, supabaseAnonKey, {
  auth: {
    autoRefreshToken: true,
    persistSession: true,
    detectSessionInUrl: true,
  },
  realtime: {
    params: {
      eventsPerSecond: 10,
    },
  },
});

// User management
export async function getCurrentUser() {
  const {
    data: { user },
    error,
  } = await supabase.auth.getUser();
  if (error) throw error;
  return user;
}

export async function signInWithPin(pin: string) {
  // For student PIN authentication, we'll use a custom approach
  // Students are stored in the children table
  const { data, error } = await supabase
    .from("children")
    .select("*")
    .eq("pin_hash", hashPin(pin))
    .eq("role", "student")
    .single();

  if (error) throw error;

  // Create a session for the student
  // In a real implementation, you'd use Supabase's custom auth
  return data;
}

export async function signInWithEmail(email: string, password: string) {
  const { data, error } = await supabase.auth.signInWithPassword({
    email,
    password,
  });
  if (error) throw error;
  return data;
}

export async function signOut() {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}

// Database helpers
export async function getBooks(filters?: {
  theme_color?: ThemeColor;
  type?: BookType;
  search?: string;
  available_only?: boolean;
}) {
  let query = supabase.from("books").select(`
      *,
      copies:copies(*)
    `);

  if (filters?.theme_color) {
    query = query.eq("theme_color", filters.theme_color);
  }

  if (filters?.type) {
    query = query.eq("type", filters.type);
  }

  if (filters?.search) {
    query = query.or(
      `title.ilike.%${filters.search}%,author.ilike.%${filters.search}%`,
    );
  }

  const { data, error } = await query.order("title");
  if (error) throw error;
  return data;
}

export async function getBookById(id: string) {
  const { data, error } = await supabase
    .from("books")
    .select(
      `
      *,
      copies:copies(
        *,
        loans:loans(
          *,
          user:users(name, class)
        )
      )
    `,
    )
    .eq("id", id)
    .single();

  if (error) throw error;
  return data;
}

export async function getUserLoans(userId: string) {
  const { data, error } = await supabase
    .from("loans")
    .select(
      `
      *,
      copy:copies(
        *,
        book:books(*)
      )
    `,
    )
    .eq("user_id", userId)
    .eq("status", "current")
    .order("due_at");

  if (error) throw error;
  return data;
}

export async function getUserCoinBalance(userId: string) {
  // Students (with coins) are in children table
  const { data, error } = await supabase
    .from("children")
    .select("coins")
    .eq("id", userId)
    .single();

  if (error) throw error;
  return data.coins;
}

export async function getCoinTransactions(userId: string, limit = 50) {
  const { data, error } = await supabase
    .from("coin_ledger")
    .select(
      `
      *,
      creator:users!created_by(name)
    `,
    )
    .eq("user_id", userId)
    .order("created_at", { ascending: false })
    .limit(limit);

  if (error) throw error;
  return data;
}

export async function createLoan(copyId: string, userId: string, dueDays = 14) {
  const dueDate = new Date();
  dueDate.setDate(dueDate.getDate() + dueDays);

  const { data, error } = await supabase
    .from("loans")
    .insert({
      copy_id: copyId,
      user_id: userId,
      role_borrower: "student", // This should be determined from user context
      due_at: dueDate.toISOString(),
      status: "current",
    })
    .select()
    .single();

  if (error) throw error;

  // Update copy status
  await supabase.from("copies").update({ status: "borrowed" }).eq("id", copyId);

  // Add coin transaction for borrowing
  await supabase.from("coin_ledger").insert({
    user_id: userId,
    delta: -5, // Cost to borrow
    type: "borrow",
    note: "Book borrowed",
    created_by: userId,
    settled: false,
  });

  return data;
}

export async function returnBook(
  loanId: string,
  condition: CopyCondition = "OK",
) {
  const now = new Date().toISOString();

  // Get loan details
  const { data: loan, error: loanError } = await supabase
    .from("loans")
    .select("*, copy:copies(*)")
    .eq("id", loanId)
    .single();

  if (loanError) throw loanError;

  const isOnTime = new Date(loan.due_at) >= new Date();

  // Update loan
  const { error: updateError } = await supabase
    .from("loans")
    .update({
      returned_at: now,
      return_condition: condition,
      status: "returned",
    })
    .eq("id", loanId);

  if (updateError) throw updateError;

  // Update copy status
  await supabase
    .from("copies")
    .update({
      status: condition === "Lost" ? "lost" : "available",
      condition: condition,
    })
    .eq("id", loan.copy_id);

  // Add coin transaction
  const coinDelta = isOnTime ? 10 : -10; // Reward for on-time return or penalty
  await supabase.from("coin_ledger").insert({
    user_id: loan.user_id,
    delta: coinDelta,
    type: isOnTime ? "return_ontime" : "overdue",
    note: isOnTime ? "Book returned on time" : "Book returned late",
    created_by: loan.user_id,
    settled: false,
  });

  return { loan, isOnTime };
}

// AI Service Integration
export async function processBookCover(imageFile: File) {
  const formData = new FormData();
  formData.append("image", imageFile);

  const { data, error } = await supabase.functions.invoke(
    "process-book-cover",
    {
      body: formData,
    },
  );

  if (error) throw error;
  return data;
}

export async function evaluateSummary(summaryText: string, bookTitle: string) {
  const { data, error } = await supabase.functions.invoke("evaluate-summary", {
    body: {
      summary: summaryText,
      book_title: bookTitle,
    },
  });

  if (error) throw error;
  return data;
}

// Utility functions
function hashPin(pin: string): string {
  // Base64 encode the PIN (matches database format)
  return btoa(pin);
}

// Real-time subscriptions
export function subscribeToUserLoans(
  userId: string,
  callback: (payload: unknown) => void,
) {
  return supabase
    .channel("user-loans")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "loans",
        filter: `user_id=eq.${userId}`,
      },
      callback,
    )
    .subscribe();
}

export function subscribeToBookAvailability(
  callback: (payload: unknown) => void,
) {
  return supabase
    .channel("book-availability")
    .on(
      "postgres_changes",
      {
        event: "*",
        schema: "public",
        table: "copies",
      },
      callback,
    )
    .subscribe();
}

// Offline support
export function enableOfflineMode() {
  // Store user data locally for offline access
  return supabase.channel("offline-sync");
}

export default supabase;
